/**
 * 全局帮助文档：path → 帮助内容
 *
 * 维护原则：
 * - 用日常语言，避免术语堆砌；术语要解释
 * - "怎么用" 写步骤，不写概念
 * - "注意" 写真实坑，不写官话
 */

import { getFeatureGuide } from './featureGuides';

export interface HelpFAQ {
  q: string;
  a: string;
}

export interface HelpEntry {
  title: string;            // "🤖 自动调参" — 通常带 emoji
  oneLiner: string;         // 一句话讲清楚
  whatItDoes: string[];     // 是什么（2-3 段）
  howToUse?: string[];      // 怎么用（步骤），可选
  metrics?: { name: string; meaning: string }[];   // 关键指标解释
  caveats?: string[];       // 注意事项 / 坑
  related?: { label: string; path: string }[];     // 相关页面
  faq?: HelpFAQ[];
}

export const HELP_REGISTRY: Record<string, HelpEntry> = {
  "/": {
    title: "🐋 概览（首页）",
    oneLiner: "一屏看全：自选股行情、AI 早报、异动归因、形态命中、新闻情绪、行业轮动",
    whatItDoes: [
      "聚合所有数据流的「今日重点」。每个区块独立刷新，可以折叠。",
      "顶部 健康条 + Tushare 用量 显示数据管道是否正常；出现 ✗ 红点 → 去 /settings 排查。",
      "三空（无自选 + 无持仓 + 无告警）的新用户会看到 3 步上手卡。",
    ],
    howToUse: [
      "自选股表格按分组 chip 筛选；点 inline「分组」名直接改组",
      "异动归因卡：💼 蓝色边框 = 你持仓的票今日有异动，附今日盈亏冲击金额",
      "顶部 ⌘K（Mac）/ Ctrl+K（Win）唤出全局搜索",
    ],
    metrics: [
      { name: "综合分（多因子）", meaning: "7 因子横截面 percentile 等权 0-100，越高越强势" },
      { name: "ML 5d/20d 预测", meaning: "机器学习模型对未来 5/20 天涨跌的预测，仅参考" },
    ],
    caveats: [
      "自选股为空时大部分卡片是空的，先去 /stocks 加几只",
      "ML 预测仅参考，自动反转过的负 IC 模型预测要反向理解",
      "看模型是不是真的准 → 去 /ml/forward-tracking 看 forward IC + 同向率",
    ],
    related: [
      { label: "自选股工作台", path: "/watchlist" },
      { label: "全市场扫描", path: "/scanner" },
      { label: "持仓账本", path: "/positions" },
      { label: "预测追踪", path: "/ml/forward-tracking" },
      { label: "⚡ 跳动归因", path: "/ml/jumps" },
    ],
  },

  "/stocks": {
    title: "📋 股票列表",
    oneLiner: "全市场 5500+ 股票搜索，支持拼音首字母（同花顺习惯）",
    whatItDoes: [
      "支持代码（600519）、名称（贵州茅台）、拼音首字母（gzmt）三种搜索",
      "勾选多只 → 批量加入自选（不用一只一只点）",
    ],
    howToUse: [
      "搜索框输入任一关键字，实时匹配",
      "点行末 ☆ 单只加自选；勾选多行后顶部 + 加入按钮批量加",
      "点名字进个股详情页（含 K 线 / 雷达 / 因子 / ML 预测 / 回测 / 标签 timeline）",
    ],
    related: [
      { label: "自选股工作台", path: "/watchlist" },
    ],
  },

  "/stocks/": {
    title: "📊 个股详情页",
    oneLiner: "增强版 K 线 + 雷达 + 因子 + 研报 AI / ML 预测时间线 + 跳动归因 + 回测 + 标签 timeline",
    whatItDoes: [
      "顶部按钮：☆ 加自选 / 📒 加交易（不用切到 /positions）",
      "已加自选时显示标签编辑器（💡 智能建议基于因子+ML+持仓自动推荐 tag）和标签变迁时间轴（含持仓事件标注）",
      "下半部依次：摘要 / 雷达 / 因子 / 卖方研报（🤖 AI 摘要 24h 缓存）/ 情感趋势 / 回测面板 / 🔮 ML 当前预测 / 📈 ML 预测时间线（含 ⚡ 跳动归因）",
      "底部 RAG 问答 — 直接对该股提问",
    ],
    howToUse: [
      "**K 线交互**（4 行 chip 控制条）：",
      "  · 区间：1M / 3M / 6M / 1Y / 全部 → 一键改 dataZoom",
      "  · 周期：日 / 周 / 月（前端聚合 OHLC）",
      "  · 对照：上证 / 沪深300 / 创业板 / 科创50 / 中证500（叠加百分比双 Y 轴）",
      "  · ⛶ 全屏（Esc 退出）",
      "  · 叠加：交易 ▲▼ / 异动 ⚡（chip 切换显示）",
      "  · 指标：BOLL（主图）/ MACD / RSI（独立子面板，多开高度自适应）",
      "  · MA：默认 5/20/60/120，7 预设 chip + 自定义周期 + localStorage 记忆",
      "**形态样本**：K 线工具栏「框选」一段 → 保存为样本（系统每晚扫全市场找相似）",
      "💡 智能建议：点开后看推荐 tag 一键加；hover 看建议依据",
      "📜 标签时间轴：横向网格，每行一个 tag，色块标记每日状态；持仓事件 ▲▼ 叠加",
      "**ML 预测时间线**：5 卡（点数/均值/σ/实际均值/同向率）+ 折线图（待对账空心 / 到期实心 / 紫色加大 = 有跳动归因）+ 最近 8 条对账表 + 跳动归因卡片",
    ],
    metrics: [
      { name: "雷达 6 维", meaning: "动量 / 反转 / RS / 量比 / 价格位置 / 回撤 6 维归一化" },
      { name: "ML 预测 IC", meaning: ">0.05 算可用；负值是反转信号已自动反向使用" },
      { name: "预测 σ", meaning: "近 N 天该股预测的标准差，σ 小 = 模型稳定，σ 大 = 来回摇摆" },
      { name: "同向率", meaning: "已对账期内预测正负方向与实际方向一致比例" },
    ],
    caveats: [
      "K 线 dataZoom 默认从 50% 起；想看全部按「全部」chip",
      "周/月聚合是前端做的（按 ISO 周或自然月），不入库；切回「日」恢复原数据",
      "指数对照 close 数据来自 index_daily 表，5 个常见指数已 backfill",
    ],
    related: [
      { label: "自选股工作台", path: "/watchlist" },
      { label: "形态样本", path: "/patterns" },
      { label: "预测追踪", path: "/ml/forward-tracking" },
      { label: "⚡ 跳动归因", path: "/ml/jumps" },
    ],
  },

  "/watchlist": {
    title: "⭐ 自选股工作台",
    oneLiner: "横向对比 + 分组/标签管理 + 批量回测 + 组合视角 + 网格扫参 + 多股 timeline 对比",
    whatItDoes: [
      "对比表 — 一眼看出池子里谁强谁弱（综合分/ML 预测/持仓状态）",
      "分组（单选）+ 标签（多选）双层组织，互不冲突",
      "选中后批量操作：复制代码 / 一键早报 / 批量回测 / 组合视角 / 改组 / 加移标签 / 多股 timeline 对比 / 网格扫参",
      "导入导出：📥 粘贴朋友的 markdown 一键加自选 / 📤 CSV 或 Markdown 导出快照分享",
    ],
    howToUse: [
      "复选框选 N 只 → 顶部蓝条出现操作按钮（sticky 滚动表格不消失）",
      "🥧 组合视角：行业分布 / 两两相关性 / 等权 vs 持仓加权 Beta / Beta 时间窗演变",
      "📊 批量回测：选策略 + Rebalance（自然漂移/月/季/日）→ 逐股 或 🎯 组合（按权重合并日收益）",
      "🔬 网格扫参：批量跑参数组合，按 Sharpe / Calmar 排序找最优",
      "📜 对比时序：选 ≥2 只看标签时序对比",
      "🏭 一键按 stock_info.industry 自动分组（如全部白酒 → 白酒组）",
    ],
    metrics: [
      { name: "组合 Beta", meaning: ">1 比大盘弹性大，<1 防御。等权 vs 持仓加权两个值" },
      { name: "RS 行业", meaning: "20 日涨幅 - 同行业均值；高 = 行业领涨股" },
    ],
    caveats: [
      "网格扫参的「最优」是历史 IS 最优，不一定未来好 — 用 /auto-tune 看 OOS 评分更可靠",
      "标签是多归属（一只股可有多个），分组是单选（一只股只属一个）",
    ],
    related: [
      { label: "全市场标签扫描", path: "/scanner" },
      { label: "持仓账本", path: "/positions" },
      { label: "自动调参", path: "/auto-tune" },
    ],
  },

  "/scanner": {
    title: "🔍 全市场标签扫描",
    oneLiner: "按系统标签条件扫全市场 5500+ 股，找符合特征的股一键批量加自选",
    whatItDoes: [
      "顶部「🔥 本周市场热点」标签：8 个最热门标签、各自最新命中数及 3 只示例股，点击即可扫描",
      "📈 7/14/30 日趋势折线：命中数前 6 个热门标签的变化（每日 19:50 自动保存快照）",
      "16 个系统标签：因子类（强势/弱势/高动量/行业领涨/突破新高/深度回撤等）+ ML 类（看涨看跌 5d/20d）+ 技术类（突破布林上轨 / MA 金叉 / 放量下跌）",
      "扫描结果带触发指标值；自动隐藏已在自选的股；勾选后批量加（可指定目标分组）",
    ],
    howToUse: [
      "点击上方热门标签或下方系统标签，即可自动扫描",
      "勾选未在自选的股 → 顶部「★ 加入自选 N 只」",
      "点击图表图例切换扫描标签",
    ],
    caveats: [
      "技术类标签（MACD/布林）SQL 窗口函数全市场计算，第一次跑稍慢",
      "趋势曲线只有 cron 跑过 N 天才有数据；首次只见 1 个数据点",
    ],
    related: [
      { label: "自选股工作台", path: "/watchlist" },
    ],
  },

  "/positions": {
    title: "📒 持仓与收益",
    oneLiner: "补录或导入交易记录，自动计算持仓、成本和收益，并持续关注持仓风险",
    whatItDoes: [
      "这是独立账本，不会连接券商或自动下单；你录入交易后，系统按平均成本法更新持仓",
      "收益变化：查看账户收益和同期沪深 300 表现",
      "可以切换 30 天、60 天、半年、一年和两年的查看范围",
      "首页异动和早报会优先标出持仓股票及其当日影响",
      "系统每周自动整理持仓股票的行情、新闻和风险变化",
    ],
    howToUse: [
      "+ 新增交易：填代码、买/卖、数量、价格；时间默认当前",
      "📂 CSV 导入：粘贴/上传（列序：ts_code,side,qty,price,fee,traded_at,note；side 兼容中文 买/卖）",
      "持仓表点「流水」看每笔历史；删一笔自动重算 position",
      "「💡 周洞察」tab：看本周自动生成的洞察；可针对单只手动「重生成」或全部持仓「批量重跑」（约 1-3 分钟）",
    ],
    metrics: [
      { name: "浮盈", meaning: "qty × (现价 - 平均成本)" },
      { name: "已实现", meaning: "卖出时锁定的盈亏累计" },
      { name: "组合收益率", meaning: "(当前总市值 + 已实现 - 手续费) / 累计净投入 - 1" },
    ],
    caveats: [
      "side 支持中文：买/卖、买入/卖出 都认",
      "删除一笔后所有后续 position 状态自动重算",
      "周洞察按 (ts_code, week_start) 唯一；同一周重生成会覆盖",
    ],
  },

  "/briefing": {
    title: "📰 自选股早报",
    oneLiner: "每天早 7:30 自动为每只自选股生成 AI 摘要：动量 / 关键新闻 / AI 评估",
    whatItDoes: [
      "Qwen-32B 读 K 线动量 + 最近 7 天新闻 + 因子排名 → 输出「今天该关注什么」",
      "支持手动重生成（如果当时数据漏了）",
      "/watchlist 选 N 只可一键批量生成",
    ],
    howToUse: [
      "默认显示最新一天；选日期可看历史",
      "点「重新生成」按钮可针对单只股重跑（消耗 LLM 调用）",
    ],
    caveats: [
      "依赖新闻管道：如果 18:30 ETL 没跑完早报会缺新闻引用",
    ],
  },

  "/news": {
    title: "📰 新闻列表",
    oneLiner: "tushare + akshare 多源新闻，自动情感打标 + 实体识别（关联到 ts_code）",
    whatItDoes: [
      "三个数据源：tushare news（财经全网）、stock_info_global_cls（财联社）、东财公告",
      "情感分两路：finbert 5 分钟 / Qwen-32B 30 分钟",
      "实体识别：自动识别提到的股票 + 关联到自选股",
    ],
    howToUse: [
      "搜索框：标题关键字 / ts_code 都可以",
      "情感筛选：正/中/负 chip",
      "点新闻条进原文",
    ],
    caveats: [
      "tushare news 限速 2 次/小时，cron 6h 一次；其他源更频繁",
    ],
  },

  "/ask": {
    title: "💬 RAG 问答",
    oneLiner: "对单股 / 标签 / 分组范围提问，LLM 基于历史新闻 + 数据回答，附引用",
    whatItDoes: [
      "BGE-M3 向量检索新闻 + Qwen-32B 生成答案；答案中股票名/代码自动变 Link 跳详情",
      "范围：全市场 / 指定 ts_code 单股 / 🏷️ 标签 / 📁 分组（限定 RAG 检索）",
      "🕘 历史：localStorage 最近 10 条问答，点列表项一键回填问题 + 范围",
      "支持 ASR：按住语音键说话，Whisper 转文字",
    ],
    howToUse: [
      '问"最近的 dckj 怎么样" 或 "短线标签里哪些股有反弹机会"',
      "答案下方会列引用的新闻条目，点击进原文核对",
      "全局 ⌘K 也可搜索历史问答",
    ],
    caveats: [
      "答案质量取决于新闻质量，垃圾问题会得垃圾答案",
      "不要把它当投资建议，是辅助理解，不是结论",
    ],
  },

  "/patterns": {
    title: "📐 形态匹配",
    oneLiner: "自定义 K 线形态样本，全市场扫相似股（DTW）+ 命中推送 + 样本对比 + 标签管理",
    whatItDoes: [
      "你在个股 K 线上框选一段（带实时归一化预览），保存为「形态样本」",
      "或 /patterns 顶部「+ 新建样本」直接选股 + 时间区间快速创建",
      "夜间 cron 19:00 跑全市场找 DTW 最近的匹配；Dashboard 顶部「今日形态匹配」卡显示",
      "命中推送：样本可开「🔔 形态命中推送」，每晚发现新出现的相似走势 → 站内信(+外部通道)",
    ],
    howToUse: [
      "**建样本**：+ 新建（选股+时间区间预设）；或个股页 K 线「框选」拉一段保存",
      "**看匹配**：进样本详情 → 相似度彩条排序；点开任一行把该股走势叠在样本曲线上对比",
      "**调质量**：详情页相似度滑块过滤 + 周期容忍预设（严格/标准/宽松，一点即重扫）",
      "**开推送**：详情页「🔔 形态命中推送」开关 + 阈值；或在 Copilot 说「把 XX 形态打开推送」",
      "**管理对比**：列表勾选多个 → 曲线叠加对比（看哪些其实长得像、可去重）+ 批量重扫；可给样本打标签、按标签筛选",
      "**Copilot 建样本**：说「把宁德时代最近 60 天走势存成形态监控」一句话建（精确不规则形态仍用框选）",
    ],
    metrics: [
      { name: "相似度 %", meaning: "DTW 距离映射 exp(-dtw/2) 到 0-100；全市场最佳匹配 ≈74%，绿≥80/蓝≥60" },
      { name: "周期容忍", meaning: "多尺度 DTW，宽松=允许更大时间伸缩，命中更多但更松" },
    ],
    caveats: [
      "命中推送默认关；要收通知得在详情页开 + 去 /settings 配通道（没配也能在站内信看）",
      "推送只推「本次新出现」的匹配（持续匹配不重复轰炸）",
    ],
    related: [
      { label: "🔮 类比预测", path: "/analog" },
      { label: "通知中心 / 站内信", path: "/notifications" },
      { label: "个股详情（框选样本）", path: "/stocks" },
    ],
  },

  "/analog": {
    title: "🔮 类比预测",
    oneLiner: "多只样本股 K 线上点 A→B→C 三点 → 这批合成原型找相似股 + 统计 B→C 历史胜率分布",
    whatItDoes: [
      "A→B = 要匹配的历史形态；B→C = 它之后怎么走。用「一批」样本（不是一只）合成原型，更稳",
      "选股：扫范围内股票当前走势，DTW 贴近原型的 top-K = 现在长得像的票",
      "预测：全历史找最像原型的 K 段，统计它们之后 c_len 天的收益分布（胜率 + 分位带）— 不是单条外推",
      "可选「去市场」：用 个股/沪深300 比值匹配，剥掉大盘 beta，只看个股自身形态",
    ],
    howToUse: [
      "搜索加入几只样本股 → 在每只 K 线图上依次点 A(起)→B(枢轴/现在)→C(未来终点)",
      "选范围（自选/全市场/板块/行业）+ 可勾去市场 → ▶ 预测",
      "看四块：原型曲线 / B→C 预测分布（大号上涨概率）/ 批成员自身分布对照 / 当前匹配选股表",
    ],
    metrics: [
      { name: "上涨概率（胜率）", meaning: "历史相似片段里 B→C 收益>0 的占比；≈50% = 无预测力" },
      { name: "分位带 p10–p90", meaning: "历史相似片段 B→C 收益的分布范围，看最差/最好可能" },
    ],
    caveats: [
      "多数形态历史胜率 ≈50%、中位 ≈0 — 工具会如实告诉你，别迷信漂亮外推",
      "批成员自身 B→C 往往偏乐观（幸存者偏差），以全历史分布为准",
      "全市场范围逐股取价 + 全历史扫窗，可能十几秒；日常用自选/行业/板块更快",
      "历史分布目前抽样上限 300 只，全市场覆盖度有限",
    ],
    related: [
      { label: "形态匹配", path: "/patterns" },
      { label: "个股详情（框选样本）", path: "/stocks" },
    ],
  },

  "/ml/experiments": {
    title: "🧪 ML 实验工作台",
    oneLiner: "训练自定义 ML 模型 + 衰减监控 + Walk-forward 评估",
    whatItDoes: [
      "新建实验 = 配训练参数（horizon / 特征子集 / 超参）→ 后台跑 train_model → 出 IC 评分",
      "📉 模型衰减监控：active 模型每天的实盘 IC vs holdout IC 对比，看是否衰减",
      "📊 Walk-forward 评估：滚动训练 + 严格 OOS 测试，判断模型是否真泛化（防过拟合）",
    ],
    metrics: [
      { name: "IC（信息系数）", meaning: "预测 vs 真实涨跌的 spearman 相关；> 0.05 可用，> 0.1 较强" },
      { name: "Holdout IC", meaning: "训练时验证集 IC，可能过拟合" },
      { name: "OOS IC", meaning: "Walk-forward 测试集 IC，更接近未来真实表现" },
    ],
    caveats: [
      "IC 负值表示模型反向有效，ml_predict 自动反转使用",
      "想让系统自动训新模型替换旧的，去 /auto-tune 的 L2 Tab",
    ],
    related: [
      { label: "自动调参", path: "/auto-tune" },
      { label: "预测追踪", path: "/ml/forward-tracking" },
    ],
  },

  "/ml/forward-tracking": {
    title: "📈 ML 预测追踪（forward tracking）",
    oneLiner: "自选股 × 模型矩阵：今日预测 + 历史波动性 + 到期实测，反作弊",
    whatItDoes: [
      "**封存 + 对账**：每天预测落库后时间戳钉死；到第 N 个交易日后用真实 close 算 realized → 对账",
      "和回测的核心差别：回测可能 look-ahead / 存活偏差 / 参数过拟合；forward tracking 不可能事后篡改",
      "「立即对账」按钮可手动触发评估（默认 cron 每天 19:30 mon-fri）",
    ],
    metrics: [
      { name: "预测 σ", meaning: "近 N 天该股预测的标准差。σ 小 = 模型对该股看法稳定；σ 大 = 来回摇摆，可信度打折" },
      { name: "同向率", meaning: "已对账期内预测正负方向与实际方向一致比例。> 60% 强；50% 抛硬币；< 50% 反指" },
      { name: "误差", meaning: "平均绝对误差（pp）= 预测涨幅与实际涨幅的差距均值" },
    ],
    caveats: [
      "刚上线的模型 / horizon 大的模型，需要等 N 个交易日才有第一条对账",
      "数据少时同向率噪声大；建议至少 ≥ 10 条到期对账后再判断",
      "看个股详细对账时间序列：点股票名进入 /stocks/:tsCode 看 ML 预测时间线面板",
    ],
    related: [
      { label: "ML 实验", path: "/ml/experiments" },
      { label: "自动调参", path: "/auto-tune" },
      { label: "⚡ 跳动归因", path: "/ml/jumps" },
    ],
  },

  "/ml/jumps": {
    title: "⚡ ML 预测跳动归因",
    oneLiner: "预测大幅变化的股票，LLM 解释为什么 — 模型 debug 工具",
    whatItDoes: [
      "**检测**：每日 ETL 后扫所有股的 5d/20d 预测，比昨天的差异 z-score >= 1.8 → 入跳动队列",
      "**归因**：拉因子 diff + 新闻 + 异动 + 日内 + 龙虎榜 + 财报 → Qwen-32B 写归因 markdown + 主因 enum",
      "Cron 每天 19:35（紧跟 ml_paper 19:30）自动跑；或手动「↻ 重跑 60 天 backfill」",
    ],
    metrics: [
      { name: "jump_z", meaning: "|今日预测 - 昨日预测| / 过去 N 日 |delta| 标准差" },
      { name: "主因 enum", meaning: "earnings / top_list / anomaly / event / news / factor_change / no_clear_cause" },
      { name: "模型健康度", meaning: "高比例可解释 (前 6 类) = 模型在响应现实；高比例 no_clear_cause = 噪音大" },
    ],
    caveats: [
      '"无明显外因" 占比超 30% 时考虑重训模型',
      "1d horizon 噪声大不归因；只跑 5d / 20d",
      "默认仅自选股，扩到全市场需改 scope=all",
    ],
    related: [
      { label: "预测追踪", path: "/ml/forward-tracking" },
      { label: "异动归因（价格波动）", path: "/" },
      { label: "ML 实验", path: "/ml/experiments" },
    ],
  },

  "/auto-tune": {
    title: "🤖 自动调参（自我学习闭环）",
    oneLiner: "L1 策略参数 + L2 ML 模型每周自动 OOS 评估 + 替换；越用越聪明",
    whatItDoes: [
      "**L1 策略参数自演化**：网格扫参 × Walk-forward → 选 OOS 最稳的策略参数 → 显著优于上次（≥+5%）才激活",
      "**L2 ML 模型自演化**：每 horizon 训新 LightGBM + walkforward 评估 → 与 active 模型最近 60 天实盘 IC 对比 → 显著优（≥+10%）才替换",
      "Cron：周日 03:00（L1）+ 03:30（L2）+ 周日 08:00 标签建议周报 + 每日 19:45 标签快照 + 19:50 hot-tags 快照",
    ],
    howToUse: [
      "切 Tab：L1 策略参数 / L2 ML 模型",
      "顶部绿色卡 = 当前激活；下方历史折叠看每次跑批的候选评分",
      "想立刻看效果：选策略/horizon → 点 🏃 立即跑（约 1-5 分钟）",
    ],
    metrics: [
      { name: "OOS score (L1)", meaning: "avg_oos_sharpe - 0.5×std；惩罚不稳定" },
      { name: "OOS score (L2)", meaning: "|avg_oos_ic| - 0.5×std；负 IC 自动反转所以取绝对值" },
      { name: "激活阈值", meaning: "L1 ≥+5%；L2 ML 模型 ≥+10%" },
    ],
    caveats: [
      'L1 自动激活需要历史 prev_score 对比；首次激活无对比直接用',
      'L2 用 active 模型最近 60 天 paper_trading 实盘 IC 当 baseline，所以模型刚激活几天内 baseline = null',
      "数据少时 OOS 噪声大 score 可能为负 — 这正是系统在告诉你「信号弱」",
    ],
    related: [
      { label: "ML 实验工作台", path: "/ml/experiments" },
      { label: "自选股工作台（手动网格扫参）", path: "/watchlist" },
    ],
  },

  "/alerts": {
    title: "🔔 价格与风险提醒",
    oneLiner: "选择一种提醒，达到价格或风险条件时自动通知你",
    whatItDoes: [
      "提供大涨、大跌、转强和转弱四种常用提醒",
      "系统每 15 分钟检查一次",
      "同一股票 24 小时内默认只提醒一次，避免重复打扰",
      "点击提醒可以直接查看对应股票或本次命中列表",
    ],
    howToUse: [
      "点模板卡 → 调阈值 + 范围（自选/全市场）→ 启用",
      "也可以直接告诉页面助手：『自选股涨超 6% 提醒我』或『综合分达到 80 时通知我』",
      "最近提醒在通知历史中查看；没有配置手机推送时，站内通知仍会保留",
    ],
    related: [
      { label: "通知中心 / 站内信", path: "/notifications" },
      { label: "🤖 页面助手", path: "/copilot" },
      { label: "设置", path: "/settings" },
    ],
  },

  "/copilot": {
    title: "🤖 AI Copilot",
    oneLiner: "自然语言 → 解析成执行计划（DAG）→ 跑出结果；50+ 能力，含建监控/告警",
    whatItDoes: [
      "说人话，LLM 解析为 plan 执行：选股 / 对比 / 找相似 / 回测 / 纸上交易 / 概念解释 / 诊股…",
      "**建监控/告警（写操作）**：「把宁德时代近 60 天走势存成形态监控」(create_pattern_sample)、"
        + "「把 XX 形态打开推送」(set_pattern_push)、「自选涨超 6% 提醒我」(create_alert_rule)、「我有哪些告警/停用 XX」(manage_alert_rule)",
      "plan 可手改后直接重跑（绕过 LLM，1000x 快）；可收藏成 chip / 设定时（到点自动跑 + 推站内信）",
      "「💬 使用帮助」模式：对话问软件怎么用，记上下文可追问",
    ],
    howToUse: [
      "执行模式输入框写需求 → ⌘/Ctrl+Enter 运行；右下角时钟标「数据截至最新交易日」",
      "结果下方可「追问」增量调整（再窄一点 / 只看主板 / 接着对比前 3 只）",
      "想持续盯：把查询设成定时，或直接说「…就提醒我」让它建告警规则",
    ],
    caveats: [
      "行业用 tushare 粗粒度分类（电气设备/半导体…），说口语名（锂电/芯片）系统会自动解析",
      "写操作里加自选走暂存确认；建形态/告警是配置类直接生效",
    ],
    related: [
      { label: "形态匹配", path: "/patterns" },
      { label: "🔮 类比预测", path: "/analog" },
      { label: "告警规则", path: "/alerts" },
    ],
  },

  "/notifications": {
    title: "🔔 通知中心",
    oneLiner: "站内信收件箱（零外部配置）+ 告警规则事件历史 + 命中率回看",
    whatItDoes: [
      "**📨 站内信**（顶部）：形态命中、自选异动归因等推送会**同时**落一份在这——没配 Bark/企微也能看到",
      "**📋 告警规则事件**：近 200 条 alert_event + 时间窗 / 规则 / 分级筛选；图标 🔥强 ❄冷 🌅早盘 🌆尾盘",
      "**📊 命中率回看**：minute_alert 各类型后 5/10/20 日真实涨跌胜率（判断告警阈值合不合理）",
    ],
    howToUse: [
      "站内信：未读红角标；点开即已读并跳到对应页；「只看未读 / 全部已读」切换",
      "告警事件：显示推送状态 📬 N 次（成功通道数）/ ⚠️ 未触达（去 /settings 配通道）",
    ],
    caveats: [
      "站内信零配置就有；外部通道（Bark/企微/TG）是「想推到手机」的可选增强",
    ],
    related: [
      { label: "告警规则", path: "/alerts" },
      { label: "设置（推送通道）", path: "/settings" },
    ],
  },

  "/settings": {
    title: "⚙️ 设置",
    oneLiner: "通知通道（Bark / 企业微信 / TG）+ Web Push 订阅 + 数据健康",
    whatItDoes: [
      "Web Push：在浏览器订阅推送（手机加桌面 PWA 后也能收）",
      "通道配置：填 Bark URL / 企业微信 webhook / TG token，测试推送",
      "订阅管理：列出所有设备订阅，可单独移除 / 测试",
    ],
    caveats: [
      "iOS Safari 需要先把 PWA Add to Home Screen 才支持 Web Push",
      "Bark 是最快上手的（iOS 装 Bark App，扫码即用）",
    ],
  },
};

export function getHelp(path: string): HelpEntry | null {
  const guide = getFeatureGuide(path);
  if (guide) return { title: guide.title, oneLiner: guide.output,
    whatItDoes: [guide.mechanism], howToUse: [guide.input, guide.next] };
  // 精确匹配优先
  if (HELP_REGISTRY[path]) return HELP_REGISTRY[path];
  // 路径前缀回退（如 /stocks/600519 → /stocks/）
  // 优先匹配最长前缀
  const sortedKeys = Object.keys(HELP_REGISTRY).sort((a, b) => b.length - a.length);
  for (const k of sortedKeys) {
    if (k !== "/" && (path.startsWith(k.endsWith('/') ? k : k + '/'))) return HELP_REGISTRY[k]!;
  }
  return null;
}
